import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { KeyboardDatePicker } from '@material-ui/pickers'
import SyncIcon from '@material-ui/icons/Sync'
import SaveIcon from '@material-ui/icons/Save'
import LaunchIcon from '@material-ui/icons/Launch'
import RoomIcon from '@material-ui/icons/Room'

const priorityMeta = {
  rush: { label: 'Rush', color: '#b71c1c', textColor: '#fff' },
  high: { label: 'High', color: '#ef6c00', textColor: '#fff' },
  normal: { label: 'Normal', color: '#1565c0', textColor: '#fff' },
  low: { label: 'Low', color: '#546e7a', textColor: '#fff' }
}

const queueStatusMeta = {
  draft: { label: 'Draft', color: '#8d6e63', textColor: '#fff' },
  ready: { label: 'Ready', color: '#2e7d32', textColor: '#fff' },
  assigned: { label: 'Assigned', color: '#6a1b9a', textColor: '#fff' },
  'in-progress': { label: 'In Progress', color: '#1565c0', textColor: '#fff' },
  partial: { label: 'Partial', color: '#f9a825', textColor: '#1f1f1f' },
  completed: { label: 'Completed', color: '#00838f', textColor: '#fff' }
}

const packetMeta = {
  ready: { label: 'Ready', color: '#43a047', textColor: '#fff' },
  missing: { label: 'Needs Info', color: '#f9a825', textColor: '#1f1f1f' }
}

const scheduleMeta = {
  overdue: { label: 'Overdue', color: '#d32f2f', textColor: '#fff' },
  never_done: { label: 'Never Done', color: '#c2185b', textColor: '#fff' },
  needs_completion: { label: 'Needs Completion', color: '#7b1fa2', textColor: '#fff' },
  due_soon: { label: 'Due Soon', color: '#fb8c00', textColor: '#fff' },
  ok: { label: 'OK', color: '#388e3c', textColor: '#fff' },
  ignored: { label: 'Ignored', color: '#78909c', textColor: '#fff' }
}

const dyeMeta = {
  yes: { label: 'Blue Dye: Yes', color: '#1e88e5', textColor: '#fff' },
  no: { label: 'Blue Dye: No', color: '#90a4ae', textColor: '#1f1f1f' },
  unspecified: { label: 'Blue Dye: Set Me', color: '#fdd835', textColor: '#1f1f1f' }
}

const missingFieldLabels = {
  site_name: 'Site Name',
  address: 'Address',
  territory: 'Territory',
  contact: 'Contact',
  map: 'Map Link',
  blue_dye: 'Blue Dye'
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    background: 'linear-gradient(180deg, #f4f6f8 0%, #eef2f6 100%)',
    minHeight: '100%'
  },
  hero: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 18,
    background: 'linear-gradient(135deg, #123c46 0%, #1f5a5f 45%, #e9f1e6 100%)',
    color: '#fff'
  },
  heroActions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  summaryCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    minHeight: 96
  },
  summaryCount: {
    fontSize: '1.8rem',
    fontWeight: 700
  },
  filterBar: {
    padding: theme.spacing(2),
    borderRadius: 14,
    marginBottom: theme.spacing(2)
  },
  mainPane: {
    alignItems: 'stretch'
  },
  tableContainer: {
    borderRadius: 14
  },
  tableRow: {
    cursor: 'pointer'
  },
  tableRowSelected: {
    backgroundColor: 'rgba(21, 101, 192, 0.08)'
  },
  siteCellTitle: {
    fontWeight: 600
  },
  sideCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    marginBottom: theme.spacing(2)
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  sectionLabel: {
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  quickFacts: {
    display: 'grid',
    gap: theme.spacing(1.5)
  },
  notesField: {
    marginTop: theme.spacing(1)
  },
  stickyPanel: {
    position: 'sticky',
    top: theme.spacing(2)
  }
}))

const HerbicideScheduleQueueDashboard = (props) => {
  const classes = useStyles()
  const { url } = props

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState('')
  const [stats, setStats] = useState({})
  const [totalCount, setTotalCount] = useState(0)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState('all')
  const [queueStatusFilter, setQueueStatusFilter] = useState('all')
  const [packetStatusFilter, setPacketStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('queuePriority')
  const [sortOrder, setSortOrder] = useState('desc')
  const [selectedItemId, setSelectedItemId] = useState(null)
  const [draft, setDraft] = useState({
    queueStatus: 'draft',
    priorityOverride: 'normal',
    blueDyePreference: 'unspecified',
    preferredSubDate: null,
    internalNotes: '',
    vendorNotes: ''
  })

  const selectedItem = items.find(item => item._id === selectedItemId) || null

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
        packetStatus: packetStatusFilter
      }
      if (scheduleStatusFilter !== 'all') params.status = scheduleStatusFilter
      if (queueStatusFilter !== 'all') params.queueStatus = queueStatusFilter
      if (searchTerm) params.search = searchTerm

      const resp = await axios.get(`${url}/api/herbicide-schedule`, { params })
      const nextItems = resp.data.items || []
      setItems(nextItems)
      setTotalCount(resp.data.total || 0)
      setSelectedItemId((currentId) => {
        if (!nextItems.length) return null
        if (!currentId || !nextItems.some(item => item._id === currentId)) return nextItems[0]._id
        return currentId
      })
    } catch (err) {
      console.error('Error fetching herbicide queue items:', err)
    }
    setLoading(false)
  }, [url, page, rowsPerPage, sortBy, sortOrder, packetStatusFilter, scheduleStatusFilter, queueStatusFilter, searchTerm])

  const fetchStats = useCallback(async () => {
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/stats`)
      setStats(resp.data || {})
    } catch (err) {
      console.error('Error fetching herbicide queue stats:', err)
    }
  }, [url])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  useEffect(() => {
    if (!selectedItem) return

    setDraft({
      queueStatus: selectedItem.queueStatus || 'draft',
      priorityOverride: selectedItem.priorityOverride || 'normal',
      blueDyePreference: selectedItem.blueDyePreference || 'unspecified',
      preferredSubDate: selectedItem.preferredSubDate ? new Date(selectedItem.preferredSubDate) : null,
      internalNotes: selectedItem.internalNotes || '',
      vendorNotes: selectedItem.vendorNotes || ''
    })
  }, [selectedItem])

  const handleSync = async () => {
    setSyncing(true)
    setSyncMessage('')
    try {
      await axios.post(`${url}/api/herbicide-schedule/sync`)
      await Promise.all([fetchItems(), fetchStats()])
      setSyncMessage('Sync complete')
      setTimeout(() => setSyncMessage(''), 5000)
    } catch (err) {
      const message = err.response?.data?.error || 'Sync failed'
      setSyncMessage(message)
      setTimeout(() => setSyncMessage(''), 10000)
    }
    setSyncing(false)
  }

  const handleSave = async () => {
    if (!selectedItem) return

    setSaving(true)
    try {
      await axios.put(`${url}/api/herbicide-schedule/${selectedItem._id}/queue`, {
        queueStatus: draft.queueStatus,
        priorityOverride: draft.priorityOverride,
        blueDyePreference: draft.blueDyePreference,
        preferredSubDate: draft.preferredSubDate,
        internalNotes: draft.internalNotes,
        vendorNotes: draft.vendorNotes
      })
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) {
      console.error('Error saving herbicide queue settings:', err)
    }
    setSaving(false)
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder(column === 'siteName' ? 'asc' : 'desc')
    }
    setPage(0)
  }

  const formatDate = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (isNaN(date.getTime())) return '—'
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  }

  const formatCurrency = (value) => {
    const amount = Number(value || 0)
    if (!amount) return '—'
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
  }

  const renderMetaChip = (meta, key, fallback) => {
    const chip = meta[key] || fallback
    if (!chip) return null

    return (
      <Chip
        size="small"
        label={chip.label}
        style={{ backgroundColor: chip.color, color: chip.textColor }}
      />
    )
  }

  const renderSummaryCard = (title, count, caption, tone) => (
    <Paper className={classes.summaryCard} style={{ borderTop: `4px solid ${tone}` }}>
      <Typography className={classes.summaryCount} style={{ color: tone }}>
        {count || 0}
      </Typography>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2" color="textSecondary">{caption}</Typography>
    </Paper>
  )

  const selectedMapHref = selectedItem?.jobsite?._id ? `/clientmapview/${selectedItem.jobsite._id}` : null

  return (
    <div className={classes.root}>
      <Paper className={classes.hero} elevation={0}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="overline" style={{ opacity: 0.9 }}>Prototype / Internal Queue View</Typography>
            <Typography variant="h4" style={{ fontWeight: 700 }}>Herbicide Sub Queue</Typography>
            <Typography variant="body1" style={{ maxWidth: 720, marginTop: 8, opacity: 0.94 }}>
              This version is built around internal dispatch prep: user priority first, then spray recency, then customer value.
              It also exposes the packet completeness the subcontractor needs before a site gets queued.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <div className={classes.heroActions}>
              {syncMessage && <Typography variant="body2">{syncMessage}</Typography>}
              <Button
                variant="contained"
                style={{ backgroundColor: '#f3f7ef', color: '#14383f' }}
                startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Data'}
              </Button>
              <Button variant="outlined" href="/herbicide-schedule" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>
                Legacy View
              </Button>
              <Button variant="outlined" href="/herbicide-territories" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>
                Territories
              </Button>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={6} md={2}>{renderSummaryCard('Ready Packets', stats.packet_ready, 'Fully prepped for the sub', '#2e7d32')}</Grid>
        <Grid item xs={6} md={2}>{renderSummaryCard('Missing Info', stats.packet_missing, 'Needs contact, map, territory, or dye', '#ef6c00')}</Grid>
        <Grid item xs={6} md={2}>{renderSummaryCard('Queue Ready', stats.queue_ready, 'Approved internally', '#43a047')}</Grid>
        <Grid item xs={6} md={2}>{renderSummaryCard('Assigned', stats.queue_assigned, 'Already placed on the sub board', '#6a1b9a')}</Grid>
        <Grid item xs={6} md={2}>{renderSummaryCard('Overdue', stats.overdue, 'Past target spray cadence', '#d32f2f')}</Grid>
        <Grid item xs={6} md={2}>{renderSummaryCard('Aspire Mismatch', stats.aspire_mismatch, 'Override and ticket are out of sync', '#f9a825')}</Grid>
      </Grid>

      <Paper className={classes.filterBar}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Schedule</InputLabel>
              <Select value={scheduleStatusFilter} onChange={(e) => { setScheduleStatusFilter(e.target.value); setPage(0) }} label="Schedule">
                <MenuItem value="all">All Schedule Statuses</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="never_done">Never Done</MenuItem>
                <MenuItem value="needs_completion">Needs Completion</MenuItem>
                <MenuItem value="due_soon">Due Soon</MenuItem>
                <MenuItem value="ok">OK</MenuItem>
                <MenuItem value="aspire_mismatch">Aspire Mismatch</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Queue</InputLabel>
              <Select value={queueStatusFilter} onChange={(e) => { setQueueStatusFilter(e.target.value); setPage(0) }} label="Queue">
                <MenuItem value="all">All Queue Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Packet</InputLabel>
              <Select value={packetStatusFilter} onChange={(e) => { setPacketStatusFilter(e.target.value); setPage(0) }} label="Packet">
                <MenuItem value="all">All Packets</MenuItem>
                <MenuItem value="ready">Packet Ready</MenuItem>
                <MenuItem value="missing">Needs Info</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => {
                  const nextSort = e.target.value
                  setSortBy(nextSort)
                  setSortOrder(nextSort === 'siteName' ? 'asc' : 'desc')
                  setPage(0)
                }}
                label="Sort"
              >
                <MenuItem value="queuePriority">Queue Priority</MenuItem>
                <MenuItem value="daysSinceLastApp">Days Since Spray</MenuItem>
                <MenuItem value="customerValue">Customer Value</MenuItem>
                <MenuItem value="preferredSubDate">Target Date</MenuItem>
                <MenuItem value="siteName">Site Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              label="Search Site / Address / Community / Building"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} className={classes.mainPane}>
        <Grid item xs={12} md={7}>
          <TableContainer component={Paper} className={classes.tableContainer}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={5}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        { id: 'siteName', label: 'Site' },
                        { id: 'queuePriority', label: 'Priority' },
                        { id: 'queueStatus', label: 'Queue' },
                        { id: 'packetStatus', label: 'Packet' },
                        { id: 'daysSinceLastApp', label: 'Days Since Spray' },
                        { id: 'customerValue', label: 'Customer $' },
                        { id: 'preferredSubDate', label: 'Target Day' }
                      ].map(column => (
                        <TableCell key={column.id}>
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!items.length ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="textSecondary" style={{ padding: 28 }}>
                            No sites match the current queue filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : items.map(item => (
                      <TableRow
                        key={item._id}
                        hover
                        className={`${classes.tableRow} ${selectedItemId === item._id ? classes.tableRowSelected : ''}`}
                        onClick={() => setSelectedItemId(item._id)}
                      >
                        <TableCell>
                          <Typography className={classes.siteCellTitle}>{item.siteName || '(unnamed)'}</Typography>
                          <Typography variant="body2" color="textSecondary">{item.address || '—'}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {item.communityName || 'No community'} {item.herbicideTerritory?.name ? `• ${item.herbicideTerritory.name}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{renderMetaChip(priorityMeta, item.priorityOverride)}</TableCell>
                        <TableCell>{renderMetaChip(queueStatusMeta, item.queueStatus)}</TableCell>
                        <TableCell>{renderMetaChip(packetMeta, item.packetStatus)}</TableCell>
                        <TableCell>
                          <Typography style={{ fontWeight: 600 }}>{item.daysSinceLastApp != null ? item.daysSinceLastApp : '—'}</Typography>
                          <Typography variant="caption" color="textSecondary">Last: {formatDate(item.lastAppliedDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography style={{ fontWeight: 600 }}>{formatCurrency(item.customerValue?.monthlyValue)}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {item.customerValue?.tier ? item.customerValue.tier.toUpperCase() : 'UNKNOWN'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{formatDate(item.preferredSubDate)}</Typography>
                          <Typography variant="caption" color="textSecondary">Next: {formatDate(item.overriddenNextDate || item.nextScheduledDate)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  onChangePage={(event, nextPage) => setPage(nextPage)}
                  onChangeRowsPerPage={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10))
                    setPage(0)
                  }}
                />
              </>
            )}
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={5}>
          <div className={classes.stickyPanel}>
            <Paper className={classes.sideCard}>
              {!selectedItem ? (
                <Typography color="textSecondary">Select a site from the queue to inspect and edit it.</Typography>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <div>
                      <Typography variant="h6" style={{ fontWeight: 700 }}>{selectedItem.siteName || '(unnamed)'}</Typography>
                      <Typography variant="body2" color="textSecondary">{selectedItem.address || 'No address on file'}</Typography>
                    </div>
                    {selectedMapHref && (
                      <Button size="small" startIcon={<RoomIcon />} href={selectedMapHref} target="_blank">
                        Map
                      </Button>
                    )}
                  </Box>

                  <Box mt={2} className={classes.chipRow}>
                    {renderMetaChip(priorityMeta, selectedItem.priorityOverride)}
                    {renderMetaChip(queueStatusMeta, selectedItem.queueStatus)}
                    {renderMetaChip(packetMeta, selectedItem.packetStatus)}
                    {renderMetaChip(scheduleMeta, selectedItem.status)}
                    {renderMetaChip(dyeMeta, selectedItem.blueDyePreference)}
                  </Box>

                  <Divider style={{ margin: '16px 0' }} />

                  <div className={classes.quickFacts}>
                    <div>
                      <Typography className={classes.sectionLabel}>Preferred Contact</Typography>
                      <Typography>{selectedItem.preferredContact?.name || 'No preferred contact found'}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedItem.preferredContact?.phone || 'No phone'} {selectedItem.preferredContact?.email ? `• ${selectedItem.preferredContact.email}` : ''}
                      </Typography>
                    </div>
                    <div>
                      <Typography className={classes.sectionLabel}>Territory / Client</Typography>
                      <Typography>{selectedItem.herbicideTerritory?.name || 'No territory assigned'}</Typography>
                      <Typography variant="body2" color="textSecondary">{selectedItem.siteInfo?.clientName || 'No client name'}</Typography>
                    </div>
                    <div>
                      <Typography className={classes.sectionLabel}>Access</Typography>
                      <Typography>{selectedItem.siteInfo?.gateCode ? `Gate code: ${selectedItem.siteInfo.gateCode}` : 'No gate code on file'}</Typography>
                      <Typography variant="body2" color="textSecondary">{selectedItem.siteInfo?.directions || 'No directions on file'}</Typography>
                    </div>
                    <div>
                      <Typography className={classes.sectionLabel}>Site Notes</Typography>
                      <Typography variant="body2" color="textSecondary">{selectedItem.siteInfo?.siteNotes || 'No site notes on file'}</Typography>
                    </div>
                  </div>
                </>
              )}
            </Paper>

            {selectedItem && (
              <>
                <Paper className={classes.sideCard}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>Queue Controls</Typography>
                  <Grid container spacing={2} style={{ marginTop: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={draft.priorityOverride}
                          onChange={(e) => setDraft({ ...draft, priorityOverride: e.target.value })}
                          label="Priority"
                        >
                          <MenuItem value="rush">Rush</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Queue Status</InputLabel>
                        <Select
                          value={draft.queueStatus}
                          onChange={(e) => setDraft({ ...draft, queueStatus: e.target.value })}
                          label="Queue Status"
                        >
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="ready">Ready</MenuItem>
                          <MenuItem value="assigned">Assigned</MenuItem>
                          <MenuItem value="in-progress">In Progress</MenuItem>
                          <MenuItem value="partial">Partial</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Blue Dye</InputLabel>
                        <Select
                          value={draft.blueDyePreference}
                          onChange={(e) => setDraft({ ...draft, blueDyePreference: e.target.value })}
                          label="Blue Dye"
                        >
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                          <MenuItem value="unspecified">Unspecified</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <KeyboardDatePicker
                        label="Sub Target Date"
                        format="MM/dd/yyyy"
                        value={draft.preferredSubDate}
                        onChange={(date) => setDraft({ ...draft, preferredSubDate: date })}
                        fullWidth
                        margin="normal"
                        inputVariant="outlined"
                        clearable
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        className={classes.notesField}
                        label="Internal Notes"
                        value={draft.internalNotes}
                        onChange={(e) => setDraft({ ...draft, internalNotes: e.target.value })}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        className={classes.notesField}
                        label="Vendor Notes"
                        value={draft.vendorNotes}
                        onChange={(e) => setDraft({ ...draft, vendorNotes: e.target.value })}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Grid>
                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      Prefs stay on the item so the next cycle can prefill blue dye and notes.
                    </Typography>
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Queue'}
                    </Button>
                  </Box>
                </Paper>

                <Paper className={classes.sideCard}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>Packet Readiness</Typography>
                  <Box mt={1} className={classes.chipRow}>
                    {selectedItem.packetStatus === 'ready' ? (
                      <Chip label="Packet ready for sub" style={{ backgroundColor: '#43a047', color: '#fff' }} />
                    ) : (
                      selectedItem.packetMissingFields.map(field => (
                        <Chip key={field} label={`Missing ${missingFieldLabels[field] || field}`} />
                      ))
                    )}
                  </Box>
                  <Divider style={{ margin: '16px 0' }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography className={classes.sectionLabel}>Spray Cadence</Typography>
                      <Typography>{selectedItem.daysSinceLastApp != null ? `${selectedItem.daysSinceLastApp} days since last spray` : 'No spray history'}</Typography>
                      <Typography variant="body2" color="textSecondary">Next target: {formatDate(selectedItem.overriddenNextDate || selectedItem.nextScheduledDate)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography className={classes.sectionLabel}>Customer Value</Typography>
                      <Typography>{formatCurrency(selectedItem.customerValue?.monthlyValue)} / month</Typography>
                      <Typography variant="body2" color="textSecondary">{formatCurrency(selectedItem.customerValue?.annualValue)} annual • {selectedItem.customerValue?.tier || 'unknown'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper className={classes.sideCard}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>Schedule Context</Typography>
                  <Box mt={1}>
                    <Typography className={classes.sectionLabel}>Dates</Typography>
                    <Typography variant="body2">Portal Last App: {formatDate(selectedItem.portalLastAppDate)}</Typography>
                    <Typography variant="body2">Aspire Last App: {formatDate(selectedItem.aspireTicketDate)}</Typography>
                    <Typography variant="body2">Scheduled Ticket: {formatDate(selectedItem.scheduledTicketDate)}</Typography>
                  </Box>
                  <Box mt={2}>
                    <Typography className={classes.sectionLabel}>Pending Tickets</Typography>
                    {selectedItem.pendingTickets?.length ? (
                      selectedItem.pendingTickets.slice(0, 3).map(ticket => (
                        <Box key={ticket.workTicketId} mb={1}>
                          <Typography variant="body2">
                            WT#{ticket.workTicketId} • {ticket.status} • {formatDate(ticket.scheduledDate)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Actual {ticket.hoursActual || 0}h • Scheduled {ticket.hoursScheduled || 0}h
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">No pending Aspire tickets on this item.</Typography>
                    )}
                  </Box>
                  <Box mt={2} display="flex" justifyContent="space-between">
                    {selectedItem.aspirePropertyId ? (
                      <Button
                        size="small"
                        endIcon={<LaunchIcon />}
                        href={`${process.env.REACT_APP_ASPIRE_BASE_URL}/app/properties/overview/${selectedItem.aspirePropertyId}`}
                        target="_blank"
                      >
                        Aspire Property
                      </Button>
                    ) : <span />}
                    <Link href="/herbicide-schedule" color="primary">Compare legacy dashboard</Link>
                  </Box>
                </Paper>
              </>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(HerbicideScheduleQueueDashboard)
